import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import { Typography } from "core/typography/Typography";
import InputSelect from "core/inputs/input-select/InputSelect";
import FafField from "./FafField";
import FafTagsInput from "./FafTagsInput";
import FafClientInput from "./FafClientInput";
import { FILTERS_FOR_NAMES } from "constants/constants";
import { formatClientOptions } from "components/form/CommonInputs";
import { searchGeneralByProfileQuery } from "api/directory/general/queries";
import { useUserContext } from "api/user/UserProvider";
import { useJobContext } from "api/settings/job/JobProvider";
import { useJobCategoryContext } from "api/settings/job-category/JobCategoryProvider";
import { getTranslatedList, withUserScopeCheck } from "utils/helper";
import {
  MIN_NEED_DASH_LINES,
  MIN_NEED_WORDS,
  countDashLines,
  countWords,
  needFieldErrors,
} from "utils/fafHelper";

// `/directory/search/general` profiles backing the client field.
const CLIENT_SEARCH_PROFILES = [
  FILTERS_FOR_NAMES.CLIENT,
  FILTERS_FOR_NAMES.PROSPECTION,
].join(",");

// Sub-form for a new need (reusable, cf. PRD-FRONT-04 combined creation).
// `need` is owned by the parent (source of truth); `onChange(patch)` bubbles the fields up.
// `showErrors` (set by the parent when navigation is blocked) triggers the per-field
// EOS error affordance (red label + info badge), without a separate red message.
const NeedForm = ({ id, need, onChange, showErrors, onRemove, embedded }) => {
  const { lang, userScope } = useUserContext();
  const { jobList } = useJobContext();
  const { getCategoryJobIds, jobCategoryList } = useJobCategoryContext();

  // The referential is FLAT since redmine 12940: categories are a plain list,
  // and jobs live in `settings_jobs`. The category only narrows the job select
  // now — the job is what gets saved.
  const roots = jobCategoryList;
  const categoryJobIds = need.category_id
    ? getCategoryJobIds(need.category_id)
    : [];
  const jobs = jobList.filter((job) => categoryJobIds.includes(job.id));

  // Clients and prospections within the user's scope (free text handled by the field).
  const getClients = async (search) => {
    try {
      const found = await withUserScopeCheck({
        callback: () =>
          searchGeneralByProfileQuery({
            profile: CLIENT_SEARCH_PROFILES,
            search,
            userScope,
          }),
        scope: userScope,
      });
      // `formatClientOptions` tags an unknown profile as a client, and only a
      // client may fill the `client_id` FK: keep the two profiles asked for.
      return formatClientOptions(
        (found ?? []).filter(({ user_type }) =>
          CLIENT_SEARCH_PROFILES.includes(user_type)
        )
      );
    } catch {
      return []; // already logged by withUserScopeCheck
    }
  };

  const dashes = countDashLines(need.description);
  const words = countWords(need.description);

  // Per-field errors derived from the single fafHelper source (no local re-derivation).
  const fieldErrors = needFieldErrors(need);
  const descriptionOk = !fieldErrors.description;
  const clientInvalid = showErrors && fieldErrors.client;
  const rootInvalid = showErrors && fieldErrors.category_id;
  const jobInvalid = showErrors && fieldErrors.job_id;
  const titleInvalid = showErrors && fieldErrors.title;
  const descriptionInvalid = showErrors && fieldErrors.description;

  return (
    <div
      className={`faf-need-form${embedded ? " faf-need-form--embedded" : ""}`}
    >
      {onRemove && (
        <div className="faf-need-form-head">
          <button
            type="button"
            className="faf-need-remove"
            onClick={onRemove}
            data-testid={`faf-need-${id}-remove`}
          >
            <FormattedMessage
              id="tools_manager_faf_remove"
              defaultMessage="Supprimer"
            />
          </button>
        </div>
      )}
      <FafField
        label={{
          id: "tools_manager_faf_need_client",
          defaultMessage: "Client",
        }}
        required
        invalid={clientInvalid}
      >
        <FafClientInput
          id={`${id}`}
          need={need}
          onChange={onChange}
          getClients={getClients}
          dataTestId={`faf-need-${id}-client`}
        />
      </FafField>

      <div className="faf-fields-2">
        <FafField
          label={{
            id: "tools_manager_faf_need_category",
            defaultMessage: "Catégorie",
          }}
          required
          invalid={rootInvalid}
        >
          <InputSelect
            id={`faf-need-cat-${id}`}
            dataTestId={`faf-need-${id}-category-root`}
            name="category_id"
            options={getTranslatedList({ lang, list: roots }) || []}
            value={need.category_id ?? ""}
            setValue={(_name, val) =>
              onChange({ category_id: val, job_id: null })
            }
          />
        </FafField>
        <FafField
          label={{ id: "tools_manager_faf_need_job", defaultMessage: "Métier" }}
          required
          invalid={jobInvalid}
        >
          <InputSelect
            id={`faf-need-job-${id}`}
            dataTestId={`faf-need-${id}-category-job`}
            name="job_id"
            options={getTranslatedList({ lang, list: jobs }) || []}
            value={need.job_id ?? ""}
            readOnly={!need.category_id}
            setValue={(_name, val) => onChange({ job_id: val })}
          />
        </FafField>
      </div>
      {roots.length === 0 && (
        <Typography
          type="p"
          className="faf-help text-3"
          label={
            <FormattedMessage
              id="tools_manager_faf_need_no_categories"
              defaultMessage="Aucune catégorie disponible pour le moment (référentiel à venir)."
            />
          }
        />
      )}

      <FafField
        label={{
          id: "tools_manager_faf_need_title",
          defaultMessage: "Titre du besoin",
        }}
        required
        invalid={titleInvalid}
      >
        <input
          className="faf-input"
          data-testid={`faf-need-${id}-title`}
          value={need.title || ""}
          onChange={(e) => onChange({ title: e.target.value })}
        />
      </FafField>

      <FafField
        label={{
          id: "tools_manager_faf_need_description",
          defaultMessage: "Description",
        }}
        required
        invalid={descriptionInvalid}
        errorMessage="tools_manager_faf_need_description_error"
      >
        <textarea
          className="faf-input faf-textarea"
          rows={3}
          data-testid={`faf-need-${id}-description`}
          value={need.description || ""}
          onChange={(e) => onChange({ description: e.target.value })}
        />
        <p className={`faf-counter ${descriptionOk ? "faf-counter--ok" : ""}`}>
          <FormattedMessage
            id="tools_manager_faf_need_description_counter"
            defaultMessage="Tirets : {dashes}/{minDashes} · Mots : {words}/{minWords}"
            values={{
              dashes,
              words,
              minDashes: MIN_NEED_DASH_LINES,
              minWords: MIN_NEED_WORDS,
            }}
          />
        </p>
      </FafField>

      <FafField
        label={{
          id: "tools_manager_faf_need_keywords",
          defaultMessage: "Mots-clés",
        }}
      >
        <FafTagsInput
          id={`faf-need-keywords-${id}`}
          dataTestId={`faf-need-${id}-keywords`}
          value={need.keywords || ""}
          onChange={(keywords) => onChange({ keywords })}
        />
      </FafField>
    </div>
  );
};

NeedForm.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  need: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  showErrors: PropTypes.bool,
  onRemove: PropTypes.func,
  embedded: PropTypes.bool,
};

export default NeedForm;
